import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { NgGenericPipeModule } from 'ng-generic-pipe';
import { MatDialog } from '@angular/material/dialog';
import { ButtonComponent } from '../../buttons/button/button.component';

@Component({
  selector: 'app-cover-hero-banner',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTooltipModule,
    NgGenericPipeModule,
    ButtonComponent,
    CommonModule,
  ],
  templateUrl: './cover-hero-banner.component.html',
  styleUrl: './cover-hero-banner.component.scss',
})
export class CoverHeroBannerComponent implements AfterViewInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  searchForm: FormGroup;
  submitClicked = false;
  isMuted = true;

  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute
  ) {
    this.searchForm = this.fb.group({
      query: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      const query = decodeURIComponent(params?.['s'] || '');
      if (query) {
        this.searchForm.patchValue({ query });
        this.onSearch();
      }
    });
  }

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = this.isMuted;
    video.defaultMuted = this.isMuted;
    video.autoplay = true;
    video.playsInline = true;
    video.loop = true;
    video.preload = 'auto';

    const tryPlay = () => {
      video.play().catch(() => {});
    };

    if (video.readyState >= 3) {
      tryPlay();
    } else {
      video.load();
      video.addEventListener('canplay', tryPlay, { once: true });
    }
  }

  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-]/g, '')
      .replace(/\-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  onSearch(): void {
    const query = this.searchForm?.get('query')?.value?.trim();
    if (query) {
      this.submitClicked = true;
      this.router.navigate(['/blog'], {
        queryParams: { s: encodeURIComponent(query) },
      });
    }
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;

    const video = this.heroVideo?.nativeElement;
    if (video) {
      video.muted = this.isMuted;
      video.defaultMuted = this.isMuted;

      if (!this.isMuted && video.volume === 0) {
        video.volume = 1;
      }

      if (video.paused) {
        video.play().catch(() => {});
      }
    }
  }
}